import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import '@mdi/font/css/materialdesignicons.css'
import 'vuetify/styles'
import { VuetifyDateAdapter } from 'vuetify/lib/composables/date/adapters/vuetify'
import { en, ja, km } from 'vuetify/locale'
import { isKhmerLocale, getAppLocale } from '@/i18n'

/**
 * Do not rely on Intl for Khmer month names:
 * in some environments, `Intl` + km still returns English month labels.
 * Keep this aligned with app locale (`@/i18n`) so date pickers always render Khmer correctly.
 */
const KHMER_MONTH_NAMES = [
  'មករា',
  'កុម្ភៈ',
  'មិនា',
  'មេសា',
  'ឧសភា',
  'មិថុនា',
  'កក្កដា',
  'សីហា',
  'កញ្ញា',
  'តុលា',
  'វិច្ឆិកា',
  'ធ្នូ',
] as const

const isKhmerUi = (): boolean => isKhmerLocale()

const khmerMonthLong = (d: Date): string => KHMER_MONTH_NAMES[d.getMonth()] ?? ''

const khmerMonthAndYear = (d: Date): string => `${khmerMonthLong(d)} ${d.getFullYear()}`

/** អាទិត្យ -> អា ... take the first grapheme for narrow weekday cells. */
const KHMER_WEEKDAY_FULL = [
  'អាទិត្យ',
  'ច័ន្ទ',
  'អង្គារ',
  'ពុធ',
  'ព្រហស្បតិ៍',
  'សុក្រ',
  'សៅរ៍',
] as const

function firstGraphemeKhmer(s: string): string {
  if (typeof Intl !== 'undefined' && 'Segmenter' in Intl) {
    const iter = new Intl.Segmenter('km-KH', { granularity: 'grapheme' }).segment(s)
    for (const { segment } of iter as Iterable<{ segment: string }>) {
      return segment
    }
  }
  return [...s][0] ?? s.slice(0, 1)
}

const KHMER_WEEKDAY_NARROW = KHMER_WEEKDAY_FULL.map((name) => firstGraphemeKhmer(name))

/** Same anchor used by Vuetify `VDatePickerMonth` (Sunday, Jan 2, 2000). */
const WEEKDAY_ROW_ANCHOR = new Date(2000, 0, 2)

/** Mirrors `weekInfo()` in `vuetify/.../adapters/vuetify.js` (default firstDay when prop is not provided). */
function vuetifyFirstDayOfWeek(adapterLocale: string): number {
  const code = adapterLocale.slice(-2).toUpperCase()
  const sun0 = 'AG AS BD BR BS BT BW BZ CA CO DM DO ET GT GU HK HN ID IL IN JM JP KE KH KR LA MH MM MO MT MX MZ NI NP PA PE PH PK PR PY SA SG SV TH TT TW UM US VE VI WS YE ZA ZW'.split(/\s+/)
  if (sun0.includes(code)) return 0
  const mon1a = 'AI AL AM AR AU AZ BA BM BN BY CL CM CN CR CY EC GE HR KG KZ LB LK LV MD ME MK MN MY NZ RO RS SI TJ TM TR UA UY UZ VN XK'.split(/\s+/)
  if (mon1a.includes(code)) return 1
  const mon1b = 'AD AN AT AX BE BG CH CZ DE DK EE ES FI FJ FO FR GB GF GP GR HU IE IS IT LI LT LU MC MQ NL NO PL RE RU SE SK SM VA'.split(/\s+/)
  if (mon1b.includes(code)) return 1
  const sat6 = 'AE AF BH DJ DZ EG IQ IR JO KW LY OM QA SD SY'.split(/\s+/)
  if (sat6.includes(code)) return 6
  if (code === 'MV') return 5
  if (code === 'PT') return 0
  return 0
}

/**
 * Override getWeekdays because Intl `narrow` for km can still return Latin S/M/T.
 * Apply only for Khmer UI while preserving Vuetify's weekday ordering logic.
 */
class ChaildDateAdapter extends VuetifyDateAdapter {
  getWeekdays(
    firstDayOfWeek?: string | number,
    weekdayFormat?: 'long' | 'short' | 'narrow',
  ): string[] {
    if (!isKhmerUi()) {
      return super.getWeekdays(firstDayOfWeek, weekdayFormat)
    }
    const n = firstDayOfWeek !== undefined && firstDayOfWeek !== '' ? Number(firstDayOfWeek) : NaN
    const adapterLocale = (this as VuetifyDateAdapter).locale
    const daysFromSunday = !Number.isNaN(n) ? n : vuetifyFirstDayOfWeek(adapterLocale)
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(WEEKDAY_ROW_ANCHOR)
      d.setDate(WEEKDAY_ROW_ANCHOR.getDate() + daysFromSunday + i)
      const dow = d.getDay()
      return KHMER_WEEKDAY_NARROW[dow]
    })
  }
}

export default createVuetify({
  components,
  directives,

  locale: {
    locale: getAppLocale(),
    fallback: 'en',
    messages: { en, ja, km },
  },

  date: {
    adapter: ChaildDateAdapter as any,
    locale: {
      en: 'en-US',
      ja: 'ja-JP',
      km: 'km-KH',
    },
    formats: {
      /** VDatePicker header — default `normalDateWithWeekday` uses Intl; km often still shows "Tue, May 5". */
      normalDateWithWeekday: (value: Date, _format: string, locale: string) => {
        if (isKhmerUi()) {
          const d = value
          const wd = KHMER_WEEKDAY_FULL[d.getDay()] ?? ''
          return `${wd}, ${khmerMonthLong(d)} ${d.getDate()}`
        }
        return new Intl.DateTimeFormat(locale, {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
        }).format(value)
      },
      monthShort: (value: Date, _format: string, locale: string) => {
        if (isKhmerUi()) return khmerMonthLong(value)
        return new Intl.DateTimeFormat(locale, { month: 'short' }).format(value)
      },
      month: (value: Date, _format: string, locale: string) => {
        if (isKhmerUi()) return khmerMonthLong(value)
        return new Intl.DateTimeFormat(locale, { month: 'long' }).format(value)
      },
      monthAndYear: (value: Date, _format: string, locale: string) => {
        if (isKhmerUi()) return khmerMonthAndYear(value)
        return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(value)
      },
      /** Year picker header/grid — Intl + km-KH can render "2026年"; use plain year like en. */
      year: (value: Date, _format: string, locale: string) => {
        if (isKhmerUi()) return String(value.getFullYear())
        return new Intl.DateTimeFormat(locale, { year: 'numeric' }).format(value)
      },
    },
  },

  theme: {
    defaultTheme: 'light',
    themes: {
      light: {
        colors: {
          primary: '#EE6090',
          secondary: '#5D6B70',
          accent: '#8c9eff',
          error: '#FF3F3A',
          lighten1: '#FFE1E0',
        },
      },
    },
  },
})
